import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SBack } from '@/components/ui/molecules/SBack';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import { MapPin, CaretRight } from 'phosphor-react';
import ordersService from '@/services/orders.service';
import { OrderResponse } from '@/types/order.types';
import { useUserLocation } from '@/hooks/useUserLocation';
import SInfiniteScroll from '@/components/ui/atoms/SInfiniteScroll';
import './MyOrders.css';

function fmtDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
}

function fmtAmount(n: number): string {
  return `$${n.toFixed(2)}`;
}

const MyOrders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(2);
  const [pageSize, setPageSize] = useState(10);
  const { location: userLocation } = useUserLocation();

  useEffect(() => {
    const fetchInitialOrders = async () => {
      try {
        setLoading(true);
        const response = await ordersService.getMyOrders(1);
        setOrders(response.data);
        setPageSize(response.pageSize);
        setPage(2);
        setHasMore(response.data.length === response.pageSize);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialOrders();
  }, []);

  const locationName = userLocation?.name || 'Tee Box';

  const renderOrderCard = useCallback((order: OrderResponse) => (
    <div key={order.id} className="mo__card" onClick={() => navigate(`/profile/orders/${order.id}`)}>
      <div className="mo__card-body">
        <div className="mo__card-head">
          <div className="mo__location-chip">
            <MapPin size={12} color="#9e9a90" weight="fill" />
            <span className="mo__location-text">Tee Box · {locationName}</span>
          </div>
          <span className="mo__badge">Paid</span>
        </div>

        <span className="mo__order-id">#{order.orderId || order.id.slice(-6).toUpperCase()}</span>

        <div className="mo__items">
          {order.items.map((item, i) => (
            <div key={i} className="mo__item">
              <span className="mo__item-name">{item.name || 'Product'}</span>
              <span className="mo__item-qty">×{item.quantity}</span>
            </div>
          ))}
        </div>

        <div className="mo__card-foot">
          <span className="mo__date">{fmtDate(order.createdAt)}</span>
          <span className="mo__total">{fmtAmount(order.totalAmount)}</span>
        </div>
      </div>

      <div className="mo__card-cta">
        <span>View Details</span>
        <CaretRight size={14} color="#9e9a90" />
      </div>
    </div>
  ), [locationName, navigate]);

  return (
    <div className="mo">
      <div className="mo__header">
        <SBack onClick={() => navigate(-1)} />
        <span className="mo__title">My Orders</span>
        <ThemeToggle />
      </div>

      {loading ? (
        <div className="mo__loading">Loading…</div>
      ) : (
        <div className="mo__body">
          {orders.length === 0 ? (
            <div className="mo__empty">No orders yet</div>
          ) : (
            <SInfiniteScroll
              data={orders}
              hasMore={hasMore}
              isLoading={isLoading}
              onLoadMore={async () => {
                setIsLoading(true);
                try {
                  const response = await ordersService.getMyOrders(page, pageSize);
                  const newOrders = response.data || [];
                  setOrders(prev => [...prev, ...newOrders]);
                  setPage(prev => prev + 1);
                  setHasMore(newOrders.length === response.pageSize);
                } catch (e) {
                  console.error(e);
                } finally {
                  setIsLoading(false);
                }
              }}
              renderItem={renderOrderCard}
              gap={12}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default MyOrders;
